import _, {Dictionary, identity, isEmpty, isEqual, isUndefined, mapValues, mergeWith} from 'lodash';
import memoize from 'memoize-one';

import {ConversationTeamStatusesEnum} from '../models/api/conversationCommonTypes';
import {
  ConversationTrackerStagesEnum,
  ConversationTrackerStatusesEnum,
} from '../models/api/conversationTrackerModel';
import {BadgeCountersModel, BoldCountersModel, CountersModel} from '../models/api/countersModel';
import {CustomViewTypesEnum} from '../models/api/customViewModel';
import {CountersVisibilityModesEnum} from '../models/api/preferencesModel';
import {InboxesFiltersEnum} from '../models/url/models/inboxesUrlModel';
import {DataState} from '../store/data/dataState';
import {buildTeammateNamespace, isTrackerUnsnoozed} from './modelHelpers';
import {findLastActivityDate, isInternalConversation} from './models/conversationHelpers';
import {isTeammateNamespace} from './models/namespaceHelpers';

/*
 * Constants.
 */

export const inboxCounterKeyPrefix = 'inb:';
export const teamCounterKeyPrefix = 'tim:';

const userSupportedVisibilities = [CountersVisibilityModesEnum.UNREAD, CountersVisibilityModesEnum.OPEN];
const teamSupportedVisibilities = [CountersVisibilityModesEnum.UNASSIGNED, CountersVisibilityModesEnum.OPEN];

export const selfInboxCounterKey = 'self_inbox';
export const selfAssignedCounterKey = 'self_assigned:mcs';
export const sharedWithMeCounterKey = 'shared_with_me:mcs';
export const discussionsCounterKey = 'internal';
export const unifiedTeammatesCounterKey = 'tea:unified';
export const draftsCounterKey = 'drafts_total';

/*
 * Helpers.
 */

export function getTimestampFromCounterUid(uid: string | undefined): Date | undefined {
  if (!uid) {
    return undefined;
  }

  // We assume that the first segment of the UID is the ISO8601 date expression.
  const iso8601 = uid.split('/')[0];

  // Perform a basic sanity check that the segment is actually an ISO8601 date expression.
  if (!iso8601.endsWith('Z')) {
    return undefined;
  }

  return new Date(iso8601);
}

function findBadgeCounters(counters: CountersModel, visibility: CountersVisibilityModesEnum) {
  const isUnread = visibility === CountersVisibilityModesEnum.UNREAD;
  return isUnread ? counters.badgeUnread : counters.badge;
}

export function findBadgeCounter(
  counters: CountersModel,
  visibility: CountersVisibilityModesEnum,
  key: string,
): number {
  const badgeCounters = findBadgeCounters(counters, visibility);
  return badgeCounters.values[key]?.value || 0;
}

export function findBadgeUid(counters: CountersModel, visibility: CountersVisibilityModesEnum, key: string) {
  const badgeCounters = findBadgeCounters(counters, visibility);
  return badgeCounters.values[key]?.uid || undefined;
}

export function findBoldCounter(
  boldCounters: BoldCountersModel,
  _visibility: CountersVisibilityModesEnum,
  key: string,
): boolean {
  const boldKey = `${key}:is_bold`;
  return boldCounters.values[boldKey]?.value || false;
}

export function findSupportedCounters(namespace: string) {
  return isTeammateNamespace(namespace) ? userSupportedVisibilities : teamSupportedVisibilities;
}

export function findDelegationBadgeCounter(counters: CountersModel, delegationId: number, key: string) {
  const delegation = counters.delegations[delegationId];
  return delegation?.values[key]?.value || 0;
}

export function buildTeammateAssignedCounterKey(teammateId: number) {
  return buildTeammateNamespace(teammateId);
}

export function buildPrivateInboxCounterKey(inboxId: number) {
  return `${inboxCounterKeyPrefix}${inboxId}:open:mcs`;
}

export function buildSharedInboxCounterKey(inboxId: number, countersVisibility: CountersVisibilityModesEnum) {
  return `${inboxCounterKeyPrefix}${inboxId}:${countersVisibility}`;
}

export function buildTeamCounterKey(teamId: number, countersVisibility: CountersVisibilityModesEnum) {
  return `${teamCounterKeyPrefix}${teamId}:${countersVisibility}`;
}

export function buildCustomViewCounterKey(
  uid: string,
  type: CustomViewTypesEnum,
  shouldHideTeamTagCounters?: boolean,
): string;
export function buildCustomViewCounterKey(
  uid: string | undefined,
  type: CustomViewTypesEnum,
  shouldHideTeamTagCounters?: boolean,
): string | undefined;
export function buildCustomViewCounterKey(
  uid: string | undefined,
  type: CustomViewTypesEnum,
  shouldHideTeamTagCounters?: boolean,
) {
  // We don't always have a view UID.
  if (!uid) {
    return undefined;
  }

  const isTeamNamespaceTag = type === CustomViewTypesEnum.TAG;

  if (isTeamNamespaceTag && shouldHideTeamTagCounters) {
    return undefined;
  }

  // If we do, check if the tag is private or shared.
  return isTeamNamespaceTag ? `view:${uid}:mcs:shared` : `view:${uid}:mcs`;
}

export function buildLensCounterKey(lensId: number, filter: InboxesFiltersEnum) {
  return `lens:${lensId}:${filter}`;
}

export function canBeImportingForCounterKey(key: string) {
  // Only self inbox, inbox, and team counter keys can be in an import-in-progress state.
  return (
    key === selfInboxCounterKey ||
    key.startsWith(inboxCounterKeyPrefix) ||
    key.startsWith(teamCounterKeyPrefix)
  );
}

/*
 * Client-side counters.
 */

export interface CounterKeys {
  badge: ReadonlyArray<string>;
  badgeUnread: ReadonlyArray<string>;
}

// Make sure to update sumCounterValue and other counter merge functions if you add properties there.
interface Counter {
  value: number;
  uid?: string;
}

export interface CounterValues {
  badge: Dictionary<Counter>;
  badgeUnread: Dictionary<Counter>;
}

export const emptyCounterKeys: CounterKeys = Object.freeze({
  badge: [],
  badgeUnread: [],
});

export const emptyCounterValues: CounterValues = Object.freeze({
  badge: {},
  badgeUnread: {},
});

/** Finds the counters that include this conversation. (Right now only works for private workspace.) */
export function findCountersForConversation(data: DataState, conversationId: number): CounterKeys {
  const conversation = data.conversations.byId[conversationId];
  if (!conversation) {
    return emptyCounterKeys;
  }

  if (conversation.teamStatus === ConversationTeamStatusesEnum.INVISIBLE) {
    return emptyCounterKeys;
  }

  const user = data.teammates[data.userId];
  if (!user) {
    return emptyCounterKeys;
  }

  const userId = user.id;
  const userNamespace = user.namespace;

  // All private workspace counters require inbox tracker status.
  const userTracker = conversation.trackers[userId];
  if (!userTracker || userTracker.status !== ConversationTrackerStatusesEnum.INBOX) {
    return emptyCounterKeys;
  }

  // TODO: Reduce duplication with isConversationInPanel().
  const counterKeys: Array<string> = [];
  const hasUserInboxTag = conversation.tagIds.includes(user.inboxTagId);
  const userOwnedInboxIds = conversation.inboxIds.filter((inboxId) => {
    const inbox = data.inboxes.byId[inboxId];
    return inbox && inbox.namespace === userNamespace;
  });

  // Inbox.
  if (hasUserInboxTag) {
    counterKeys.push(selfInboxCounterKey);
  }

  // Assigned to me.
  if (hasUserInboxTag && userTracker.stage === ConversationTrackerStagesEnum.ASSIGNEE) {
    counterKeys.push(selfAssignedCounterKey);
  }

  // Shared with me.
  if (
    hasUserInboxTag &&
    userTracker.stage !== ConversationTrackerStagesEnum.ASSIGNEE &&
    !isInternalConversation(conversation) &&
    userOwnedInboxIds.length === 0
  ) {
    counterKeys.push(sharedWithMeCounterKey);
  }

  // Discussions.
  if (
    hasUserInboxTag &&
    userTracker.stage !== ConversationTrackerStagesEnum.ASSIGNEE &&
    isInternalConversation(conversation)
  ) {
    counterKeys.push(discussionsCounterKey);
  }

  // Private inboxes.
  if (hasUserInboxTag && userTracker.stage !== ConversationTrackerStagesEnum.ASSIGNEE) {
    counterKeys.push(...userOwnedInboxIds.map(buildPrivateInboxCounterKey));
  }

  // Custom views.
  counterKeys.push(
    ..._(conversation.tagIds)
      .map((tagId) => {
        const customView = data.customViews[tagId];
        return customView && buildCustomViewCounterKey(customView.uid, customView.type);
      })
      .compact()
      .value(),
  );

  // TODO: Reduce duplication with findUnreadStatus().
  const isUserUnread =
    isTrackerUnsnoozed(conversation, userTracker) ||
    !conversation.seenAt ||
    findLastActivityDate(conversation, userId) > conversation.seenAt;

  return {
    badge: counterKeys,
    badgeUnread: isUserUnread ? counterKeys : [],
  };
}

export function computeCountersForConversations(
  data: DataState,
  conversationIds: ReadonlyArray<number>,
): CounterValues {
  const results = conversationIds.map((id) => findCountersForConversation(data, id));

  return {
    badge: _(results)
      .flatMap((result) => result.badge)
      .countBy(identity)
      .mapValues((value) => ({value}))
      .value(),
    badgeUnread: _(results)
      .flatMap((result) => result.badgeUnread)
      .countBy(identity)
      .mapValues((value) => ({value}))
      .value(),
  };
}

export function createCounterComputerForConversations(conversationIds: ReadonlyArray<number>) {
  return memoize((data: DataState): CounterValues => computeCountersForConversations(data, conversationIds));
}

export function negateCounterValues(values: CounterValues): CounterValues {
  return {
    badge: mapValues(values.badge, (b) => ({...b, value: -b.value})),
    badgeUnread: mapValues(values.badgeUnread, (b) => ({...b, value: -b.value})),
  };
}

export function sumCounterValues(...values: ReadonlyArray<CounterValues>): CounterValues {
  return {
    badge: sumCounterValuesBase(...values.map((v) => v.badge)),
    badgeUnread: sumCounterValuesBase(...values.map((v) => v.badgeUnread)),
  };
}

function sumCounterValuesBase(...values: ReadonlyArray<Dictionary<Counter>>): Dictionary<Counter> {
  return mergeWith({}, ...values, sumCounterValue);
}

function sumCounterValue(objValue: any, srcValue: any, key: string): number | undefined {
  // Only add counter values. Let Lodash handle the rest.
  if (key !== 'value') {
    return undefined;
  }

  return (objValue ?? 0) + (srcValue ?? 0);
}

export function addCounterValuesToCounters(
  counters: CountersModel,
  ...values: ReadonlyArray<CounterValues>
): CountersModel {
  return {
    ...counters,
    badge: addCounterValuesToCountersBase(counters.badge, ...values.map((v) => v.badge)),
    badgeUnread: addCounterValuesToCountersBase(counters.badgeUnread, ...values.map((v) => v.badgeUnread)),
  };
}

function addCounterValuesToCountersBase(
  counters: BadgeCountersModel,
  ...values: ReadonlyArray<Dictionary<Counter>>
): BadgeCountersModel {
  if (isEmpty(values)) {
    return counters;
  }

  return {
    ...counters,
    values: _({})
      // Sum all values.
      .mergeWith(counters.values, ...values, sumCounterValue)
      // Clamp to 0.
      .mapValues((counter) => {
        if (isUndefined(counter)) {
          return undefined;
        }

        return {
          ...counter,
          value: Math.max(0, counter.value),
        };
      })
      .value(),
  };
}

export function addCounterValuesToClientCounters(
  counters: CountersModel,
  clientCounterValues: CounterValues,
  newValues: CounterValues,
): CounterValues {
  return {
    badge: addCounterValuesToClientCountersBase(
      counters.badge.values,
      clientCounterValues.badge,
      newValues.badge,
    ),
    badgeUnread: addCounterValuesToClientCountersBase(
      counters.badgeUnread.values,
      clientCounterValues.badgeUnread,
      newValues.badgeUnread,
    ),
  };
}

function addCounterValuesToClientCountersBase(
  counterValues: Dictionary<Counter | undefined>,
  clientCounterValues: Dictionary<Counter>,
  newValues: Dictionary<Counter>,
): Dictionary<Counter> {
  if (isEmpty(newValues)) {
    return clientCounterValues;
  }

  return {
    ...clientCounterValues,
    ..._(newValues)
      .toPairs()
      // Filter out zeroes.
      .filter(([, delta]) => delta.value !== 0)
      .map(([key, delta]) => {
        // Take either the previously frozen value, or the current value from the real counters.
        const counter = clientCounterValues[key] ?? counterValues[key] ?? {value: 0};

        const value = Math.max(0, counter.value + delta.value);

        return [key, {...counter, value}];
      })
      .fromPairs()
      .value(),
  };
}

export function createCountersUpdaterForConversations(
  conversationIds: ReadonlyArray<number>,
  conversationsUpdater: (state: DataState) => DataState,
): (state: DataState) => DataState {
  // Memoized function that computes the counters for the given conversations.
  const counterComputer = createCounterComputerForConversations(conversationIds);

  // Memoized updater that applies the counter deltas for the given conversations.
  const countersUpdater = memoize(
    (
      counters: CountersModel,
      beforeCounterValues: CounterValues,
      afterCounterValues: CounterValues,
    ): CountersModel => {
      // Subtract the old counters, add the new counters.
      const beforeCounterDeltas = negateCounterValues(beforeCounterValues);
      const afterCounterDeltas = afterCounterValues;
      return addCounterValuesToCounters(counters, beforeCounterDeltas, afterCounterDeltas);
    },
    (a, b) => isEqual(a[0], b[0]) && isEqual(a[1], b[1]) && isEqual(a[2], b[2]),
  );

  // Memoized updater that updates the counters state.
  const dataCountersUpdater = memoize((data: DataState, newCounters: CountersModel): DataState => {
    if (data.counters === newCounters) {
      return data;
    }

    return {
      ...data,
      counters: newCounters,
    };
  });

  // Everything combined.
  return (data: DataState) => {
    const newData = conversationsUpdater(data);

    const beforeCounterValues = counterComputer(data);
    const afterCounterValues = counterComputer(newData);
    const newCounters = countersUpdater(newData.counters, beforeCounterValues, afterCounterValues);
    const newDataWithNewCounters = dataCountersUpdater(newData, newCounters);

    return newDataWithNewCounters;
  };
}
